/**
 * The design-system kit. Every token comes from styles/tokens.css — nothing here
 * improvises a colour, a size or a duration.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Download, ArrowUpRight } from 'lucide-react';
import { useCountUp, useReveal, useMagnetic } from '../lib/hooks';
import assets from '../data/assets.json';
import { categoryLabel } from '../lib/format';

/* ── Reveal wrapper ─────────────────────────────────────────────────────── */
export function Reveal({ children, delay = 0, className = '', ...rest }) {
  const [ref, revealed] = useReveal();
  return (
    <div
      ref={ref}
      className={`c-reveal ${revealed ? 'is-revealed' : ''} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
      {...rest}
    >
      {children}
    </div>
  );
}

/* ── Eyebrow / section header ───────────────────────────────────────────── */

/**
 * Mono labels are uppercased by the type system, which would render the
 * masterbrand as "SUPRAI". Casing is a hard rule: the AI entity is exactly
 * `SuprAI`. Any label containing it keeps its own case.
 */
export function monoCaseStyle(text) {
  return /SuprAI/.test(String(text ?? '')) ? { textTransform: 'none' } : undefined;
}

export function Eyebrow({ children, onNavy = false, className = '', style }) {
  return (
    <span
      className={`c-eyebrow ${onNavy ? 'c-eyebrow--on-navy' : ''} ${className}`}
      style={{ ...monoCaseStyle(children), ...style }}
    >
      {children}
    </span>
  );
}

export function SectionHeader({
  eyebrow,
  title,
  intro,
  onNavy = false,
  level = 2,
  align = 'left',
  id,
  className = '',
}) {
  const Heading = `h${level}`;
  return (
    <div
      className={className}
      style={{
        maxWidth: '820px',
        margin: align === 'center' ? '0 auto' : undefined,
        textAlign: align,
      }}
    >
      {eyebrow ? <Eyebrow onNavy={onNavy}>{eyebrow}</Eyebrow> : null}
      {title ? (
        <Heading id={id} style={{ marginTop: eyebrow ? 12 : 0 }}>
          {title}
        </Heading>
      ) : null}
      {intro ? (
        <p className={`t-body-lg ${onNavy ? 't-on-navy-secondary' : 't-secondary'}`} style={{ marginTop: 16 }}>
          {intro}
        </p>
      ) : null}
    </div>
  );
}

/* ── Buttons ────────────────────────────────────────────────────────────── */
const VARIANTS = {
  primary: 'c-btn--primary',
  secondary: 'c-btn--secondary',
  'secondary-on-navy': 'c-btn--secondary-on-navy',
  tertiary: 'c-btn--tertiary',
  'tertiary-on-navy': 'c-btn--tertiary-on-navy',
};

export function Button({ as, to, href, variant = 'primary', magnetic = false, className = '', children, ...rest }) {
  const magneticRef = useMagnetic(6);
  const classes = `c-btn ${VARIANTS[variant] || VARIANTS.primary} ${className}`;
  const ref = magnetic && variant === 'primary' ? magneticRef : undefined;

  if (to) {
    return (
      <Link ref={ref} to={to} className={classes} {...rest}>
        {children}
      </Link>
    );
  }
  if (href) {
    const external = /^https?:/i.test(href);
    return (
      <a
        ref={ref}
        href={href}
        className={classes}
        {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        {...rest}
      >
        {children}
      </a>
    );
  }
  const Tag = as || 'button';
  return (
    <Tag ref={ref} className={classes} {...rest}>
      {children}
    </Tag>
  );
}

/* ── Cards ──────────────────────────────────────────────────────────────── */
export function Card({ children, hover = false, feature = false, className = '', ...rest }) {
  return (
    <div
      className={`c-card ${hover ? 'c-card--hover' : ''} ${feature ? 'c-card--feature' : ''} ${className}`}
      {...rest}
    >
      {children}
    </div>
  );
}

export function NavyCard({ children, hover = false, className = '', ...rest }) {
  return (
    <div className={`c-card-navy ${hover ? 'c-card-navy--hover' : ''} ${className}`} {...rest}>
      {children}
    </div>
  );
}

export function Chip({ children, onNavy = false }) {
  return <span className={`chip ${onNavy ? 'chip--on-navy' : ''}`}>{children}</span>;
}

/* ── Stat tile — the final value is the no-JS fallback ──────────────────── */
export function StatTile({ value, suffix = '', literal, label }) {
  const [ref, current] = useCountUp(value || 0);
  return (
    <div className="stat-tile" ref={literal ? undefined : ref} data-testid="stat-tile">
      <div className="stat-tile__value">{literal || `${current}${suffix}`}</div>
      <div className="stat-tile__label">{label}</div>
    </div>
  );
}

/* ── Badge ──────────────────────────────────────────────────────────────── */
export function Badge({ category, children }) {
  return (
    <span className={`c-badge ${category ? `c-badge--${category}` : ''}`}>
      {children || categoryLabel(category)}
    </span>
  );
}

/* ── Pending content ────────────────────────────────────────────────────── */
export function PendingContent({ children, label = 'Content pending', onNavy = false, className = '' }) {
  return (
    <div
      className={`c-pending ${onNavy ? 'c-pending--on-navy' : ''} ${className}`}
      role="note"
      data-testid="pending-content"
    >
      <span className="c-pending__label">{label}</span>
      <p>{children}</p>
    </div>
  );
}

/** An em-dash with a mono "To be confirmed" tooltip — never a token. */
export function PendingInline({ title = 'To be confirmed' }) {
  return (
    <abbr className="c-pending-inline" title={title} data-testid="pending-inline">
      —
    </abbr>
  );
}

/* ── Disclaimer ─────────────────────────────────────────────────────────── */
export function Disclaimer({ children, onNavy = false, title = 'Disclaimer' }) {
  return (
    <aside className={`c-disclaimer ${onNavy ? 'c-disclaimer--on-navy' : ''}`} aria-label={title}>
      <span className="t-micro" style={{ display: 'block', marginBottom: 8 }}>
        {title}
      </span>
      <p>{children}</p>
    </aside>
  );
}

/* ── DataTable — navy header row, white bold text ───────────────────────── */
export function DataTable({ caption, columns, rows }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="c-table" data-testid="data-table">
        {caption ? <caption>{caption}</caption> : null}
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col} scope="col">
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.key || i}>
              <th scope="row">{row.label}</th>
              <td className={row.mono ? 't-mono' : undefined}>{row.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── DocumentRow — a real <a download> when a file exists; an explicitly
   disabled control with aria-disabled and a mono caption when it does not.
   Never plain grey text masquerading as a link. ─────────────────────────── */
export function DocumentRow({
  title,
  type,
  size,
  date,
  fileUrl,
  linkTo,
  linkLabel,
  pendingCaption = 'Document pending',
}) {
  return (
    <div className="doc-row" data-testid="document-row">
      <span className="doc-row__icon" aria-hidden="true">
        <FileText size={18} />
      </span>
      <div className="doc-row__main">
        <p style={{ fontWeight: 500, color: 'var(--text-heading)' }}>{title}</p>
        <p className="t-mono t-caption" style={{ fontSize: 12, marginTop: 4 }}>
          {[type, size, date].filter(Boolean).join(' · ') || pendingCaption}
        </p>
      </div>
      {fileUrl ? (
        <a href={fileUrl} download className="c-btn c-btn--tertiary" aria-label={`Download ${title}`}>
          <Download size={16} aria-hidden="true" /> Download
        </a>
      ) : linkTo ? (
        <Link to={linkTo} className="c-btn c-btn--tertiary" aria-label={`${linkLabel}: ${title}`}>
          {linkLabel}
        </Link>
      ) : (
        <a
          href="#pending"
          download
          aria-disabled="true"
          tabIndex={-1}
          onClick={(e) => e.preventDefault()}
          className="c-btn c-btn--tertiary"
          style={{ color: 'var(--action-disabled-text)', cursor: 'not-allowed' }}
        >
          <Download size={16} aria-hidden="true" /> Download
        </a>
      )}
    </div>
  );
}

/* ── Timeline ───────────────────────────────────────────────────────────── */
export function Timeline({ items }) {
  return (
    <ol className="timeline" data-testid="timeline">
      <span className="timeline__spine" aria-hidden="true" />
      {items.map((item, i) => (
        <li
          key={`${item.text}-${i}`}
          className={`timeline__item ${item.pending || !item.year ? 'timeline__item--pending' : ''}`}
        >
          <span className="timeline__dot" aria-hidden="true" />
          <p className="t-mono" style={{ fontSize: 13, color: 'var(--signal-blue)' }}>
            {item.year || <PendingInline />}
          </p>
          <p style={{ marginTop: 4 }}>{item.text}</p>
          {item.detail ? (
            <p className="t-small t-secondary" style={{ marginTop: 4 }}>
              {item.detail}
            </p>
          ) : null}
        </li>
      ))}
    </ol>
  );
}

/* ── Img — self-hosted WebP with a 2x srcset and a recorded remote fallback ─ */
export function Img({ assetKey, alt, className = '', style, sizes, loading = 'lazy', fit }) {
  const record = assets[assetKey];
  const [failed, setFailed] = useState(false);
  if (!record) return null;
  const src = failed ? record.fallback : record.src;
  const srcSet =
    !failed && record.src2x && record.src2x !== record.src ? `${record.src} 1x, ${record.src2x} 2x` : undefined;
  return (
    <img
      src={src}
      srcSet={srcSet}
      sizes={sizes}
      alt={alt}
      width={record.w || undefined}
      height={record.h || undefined}
      loading={loading}
      decoding="async"
      className={className}
      style={{ ...(fit ? { objectFit: fit } : {}), ...style }}
      onError={() => setFailed(true)}
    />
  );
}

/* ── PersonCard — normalised 4:5 crop, object-position center top ───────── */
export function PersonCard({ assetKey, name, role, org = 'SII', photoUrl, bio }) {
  return (
    <article className="c-card" style={{ padding: 0, overflow: 'hidden' }} data-testid="person-card">
      <div className="person-card__media">
        {assetKey ? (
          <Img assetKey={assetKey} alt={`Portrait of ${name}, ${role} at ${org}`} />
        ) : photoUrl ? (
          <img src={photoUrl} alt={`Portrait of ${name}, ${role} at ${org}`} loading="lazy" />
        ) : null}
      </div>
      <div style={{ padding: 'var(--space-lg)' }}>
        <h4>{name}</h4>
        <p className="t-mono t-secondary" style={{ fontSize: 13, marginTop: 4 }}>
          {role}
        </p>
        {bio ? (
          <p className="t-small t-secondary" style={{ marginTop: 12 }}>
            {bio}
          </p>
        ) : null}
      </div>
    </article>
  );
}

/* ── External link with the required attributes ─────────────────────────── */
export function ExternalLink({ href, children, className = '' }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
      {children}
      <ArrowUpRight size={14} aria-hidden="true" style={{ display: 'inline', marginLeft: 4 }} />
      <span className="u-sr-only"> (opens in a new tab)</span>
    </a>
  );
}
